import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Heart, X, Star, ArrowCounterClockwise, SealCheck, MapPin, Fire } from "@phosphor-icons/react";
import { mediaUrl } from "@/pages/match/MatchOnboarding";

export default function MatchDiscovery({ profile, onChanged }) {
  const qc = useQueryClient();
  const [idx, setIdx] = useState(0);
  const [photo, setPhoto] = useState(0);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["match-discovery"],
    queryFn: async () => (await api.get("/match/discovery")).data,
  });
  const deck = data?.deck || [];
  const card = deck[idx];

  const advance = () => { setPhoto(0); setIdx((i) => i + 1); };

  const act = useMutation({
    mutationFn: async ({ path, target_id }) => (await api.post(path, target_id ? { target_id } : {})).data,
    onSuccess: (res, vars) => {
      if (res.matched) toast.success(`It's a match with ${res.match?.with_name || "them"}! 🎉 Say hello in Matches.`, { duration: 5000 });
      qc.invalidateQueries({ queryKey: ["match-me"] });
      if (vars.path === "/match/rewind") { refetch(); setIdx((i) => Math.max(0, i - 1)); }
      else advance();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  if (isLoading) return <div className="py-16 text-center text-muted-foreground">Finding people for you…</div>;

  if (!card) {
    return (
      <div className="rounded-3xl border border-border bg-card p-10 text-center" data-testid="match-discovery-empty">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-secondary text-muted-foreground"><Heart size={28} /></div>
        <h3 className="font-display text-lg font-semibold">You're all caught up</h3>
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">No more profiles right now. Widen your preferences or check back soon — new members join daily.</p>
        <button data-testid="match-discovery-refresh" onClick={() => { setIdx(0); refetch(); }} className="mt-4 rounded-full border border-border px-4 py-2 text-sm font-medium hover:border-rose-500">Refresh</button>
      </div>
    );
  }

  const rem = profile?.likes_remaining;
  const boost = profile?.boost;

  return (
    <div className="mx-auto max-w-md" data-testid="match-discovery">
      <div className="mb-3 flex items-center justify-between text-xs text-muted-foreground">
        <span data-testid="match-likes-remaining">{rem === "unlimited" ? "Unlimited likes" : `${rem} likes left today`}</span>
        {boost?.active ? <span className="inline-flex items-center gap-1 font-semibold text-orange-500"><Fire size={13} weight="fill" /> Boost active</span> : null}
      </div>

      <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-lg" data-testid={`match-card-${card.user_id}`}>
        <div className="relative aspect-[4/5] bg-secondary">
          {card.photos?.length ? (
            <img src={mediaUrl(card.photos[photo]?.url)} alt={card.display_name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-6xl font-display text-muted-foreground/40">{card.display_name?.charAt(0)}</div>
          )}
          {card.photos?.length > 1 && (
            <>
              <div className="absolute inset-x-3 top-3 flex gap-1">
                {card.photos.map((_, i) => <div key={i} className={`h-1 flex-1 rounded-full ${i === photo ? "bg-white" : "bg-white/40"}`} />)}
              </div>
              <button onClick={() => setPhoto((p) => Math.max(0, p - 1))} className="absolute left-0 top-0 h-full w-1/2" aria-label="Previous photo" />
              <button onClick={() => setPhoto((p) => Math.min(card.photos.length - 1, p + 1))} className="absolute right-0 top-0 h-full w-1/2" aria-label="Next photo" />
            </>
          )}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4 text-white">
            <h3 className="flex items-center gap-2 font-display text-2xl font-semibold">
              {card.display_name}, {card.age}
              {card.verified && <SealCheck size={20} weight="fill" className="text-sky-400" />}
            </h3>
            <p className="mt-0.5 flex items-center gap-3 text-sm text-white/85">
              {card.city && <span className="inline-flex items-center gap-1"><MapPin size={13} weight="fill" /> {card.city}{card.country ? `, ${card.country}` : ""}</span>}
              <span className="capitalize">{card.intention}</span>
            </p>
          </div>
        </div>
        <div className="space-y-3 p-4">
          {card.bio && <p className="text-sm">{card.bio}</p>}
          {card.interests?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {card.interests.map((t) => <span key={t} className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium capitalize">{t}</span>)}
            </div>
          )}
          {card.languages?.length > 0 && <p className="text-xs text-muted-foreground">Speaks: <span className="capitalize">{card.languages.join(", ")}</span></p>}
          {card.prompts?.map((p, i) => (
            <div key={i} className="rounded-xl bg-secondary/60 p-3">
              <p className="text-xs font-medium text-muted-foreground">{p.q}</p>
              <p className="mt-0.5 text-sm">{p.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-3">
        <ActBtn testid="match-rewind" title="Undo" onClick={() => act.mutate({ path: "/match/rewind" })} disabled={act.isPending}
          className="border-amber-400 text-amber-500"><ArrowCounterClockwise size={20} weight="bold" /></ActBtn>
        <ActBtn testid="match-pass" title="Pass" size="lg" onClick={() => act.mutate({ path: "/match/pass", target_id: card.user_id })} disabled={act.isPending}
          className="border-slate-300 text-slate-500 dark:border-slate-600"><X size={28} weight="bold" /></ActBtn>
        <ActBtn testid="match-superlike" title="Super Like" onClick={() => act.mutate({ path: "/match/superlike", target_id: card.user_id })} disabled={act.isPending}
          className="border-sky-400 text-sky-500"><Star size={22} weight="fill" /></ActBtn>
        <ActBtn testid="match-like" title="Like" size="lg" onClick={() => act.mutate({ path: "/match/like", target_id: card.user_id })} disabled={act.isPending}
          className="border-rose-400 text-rose-500"><Heart size={28} weight="fill" /></ActBtn>
      </div>
    </div>
  );
}

function ActBtn({ children, onClick, disabled, className, size = "md", title, testid }) {
  const s = size === "lg" ? "h-16 w-16" : "h-12 w-12";
  return (
    <button data-testid={testid} title={title} onClick={onClick} disabled={disabled}
      className={`flex ${s} items-center justify-center rounded-full border-2 bg-card shadow-sm transition-transform hover:scale-110 disabled:opacity-40 ${className}`}>
      {children}
    </button>
  );
}
